import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMountEffect } from '../../hooks/useMountEffect'

import type { AxiosError } from 'axios'
import { PiBuildings, PiCircleNotch, PiListChecks } from 'react-icons/pi'
import { RightPanel, type RightPanelState } from './RightPanel'
import { applyRecommendation } from '../../services/recommendations_api'
import { notifySuccess } from '../../services/toast'
import { cn } from '../UI/cn'
import { PillTabs, TabPanel } from '../FbUI/PillTabs'
import { secondaryPillButtonClass } from '../FbUI/secondaryPillButton'
import { Ds2026SearchInput } from '../FbUI/Ds2026SearchInput'
import {
  getLatestActionSummary,
  getRecommendationRefreshStatus,
  ActionSummaryData,
} from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import {
  ApplyRecommendationPanel,
  EnrichedActionCard,
  EnrichedActionCardSkeleton,
} from './EnrichedActionCard'
import { SummaryStatsHeader } from './SummaryStatsHeader'
import { URGENCY_ICONS } from './constants'
import { resolveAgentIcon } from './agentIcons'
import useApiService from '../../services/api'
import { useRecommendationsPolling } from './useRecommendationsPolling'
import { useActionClickManager } from './useActionClickManager'
import { useAggregationRefresh } from '../DataTable/hooks/useAggregationRefresh'
import {
  groupActionsByAgent,
  groupActionsByCompany,
  partitionActionsByStatus,
  sortByUrgencyScore,
} from './recommendationVisibility'

const REC_TABS_ID = 'recommendations-view'

type ViewMode = 'priority' | 'signal' | 'company'

/**
 * Group resolved actions by their completion day (`resolved_at`), newest day
 * first, with a human label per the activity-timeline convention
 * ("Today" / "Yesterday" / weekday, plus the short date). Used to give the
 * recently-completed list timeline-style day headers.
 */
const groupByCompletionDay = (actions: EnrichedAction[]) => {
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  const resolvedTs = (a: EnrichedAction) =>
    a.resolved_at ? new Date(a.resolved_at).getTime() : 0

  // Newest first, so within each day the most recent completion sits on top
  // (days themselves are ordered newest-first below).
  const groups = new Map<string, EnrichedAction[]>()
  for (const a of [...actions].sort((x, y) => resolvedTs(y) - resolvedTs(x))) {
    const key = a.resolved_at
      ? new Date(a.resolved_at).toDateString()
      : 'unknown'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(a)
  }

  return [...groups.entries()]
    .map(([key, items]) => {
      if (key === 'unknown') return { key, label: 'Earlier', ts: 0, items }
      const date = new Date(key)
      const relative =
        date.toDateString() === today.toDateString()
          ? 'Today'
          : date.toDateString() === yesterday.toDateString()
            ? 'Yesterday'
            : date.toLocaleDateString('en-US', { weekday: 'long' })
      const display = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(date.getFullYear() === today.getFullYear()
          ? {}
          : { year: 'numeric' }),
      })
      const label = relative !== display ? `${relative}, ${display}` : relative
      return { key, label, ts: date.getTime(), items }
    })
    .sort((a, b) => b.ts - a.ts)
}

const URGENCY_BANDS: Array<{
  key: EnrichedAction['urgency']
  label: string
}> = [
  { key: 'do_now', label: 'Hot' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
]

interface RecommendationsCardProps {
  className?: string
  /** Optional user ID to view another user's summary (superusers only) */
  userId?: number
  /** Callback fired whenever the summary changes */
  onSummaryLoaded?: (summary: ActionSummaryData | null) => void
  /** Content rendered on the left of the fixed header row (e.g. "Action Center" title). */
  headerSlot?: React.ReactNode
  /** Content rendered on the far right of the fixed header row (e.g. "Manage AI Agents" button). */
  headerTrailingSlot?: React.ReactNode
  /** Controlled right-panel state. Tagged union so the card can ask
   *  the parent to open different kinds of panels (recently-completed,
   *  apply-rec, etc.) without lifting per-kind state. Falls back to
   *  internal state when omitted so the card still works standalone. */
  activePanel?: RightPanelState
  onActivePanelChange?: (next: RightPanelState) => void
  /** When the parent provides this ref, the right panel content is
   *  portaled into ``ref.current`` so it lives as a flex sibling of
   *  the parent's main content (no fixed-position clipping artifacts
   *  against the App Shell's rounded corners). */
  resolvedSiderHostRef?: React.RefObject<HTMLDivElement | null>
}

/**
 * Displays AI-generated daily recommendations for the current user.
 *
 * Shows prioritized action items grouped by urgency (default), signal type, or company.
 * All three views share the same flat design language: inline group headers + card lists.
 *
 * Superusers can pass a userId prop to view another user's summary.
 */
export const RecommendationsCard: React.FC<RecommendationsCardProps> = ({
  className,
  userId,
  onSummaryLoaded,
  headerSlot,
  headerTrailingSlot,
  activePanel: activePanelProp,
  onActivePanelChange,
  resolvedSiderHostRef,
}) => {
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<ViewMode>('priority')
  // Controlled when the parent supplies ``activePanel`` + setter;
  // otherwise falls back to internal state so the component still
  // works standalone (e.g. component-catalog previews, tests).
  const [internalActivePanel, setInternalActivePanel] =
    useState<RightPanelState>({ kind: 'closed' })
  const isControlled =
    activePanelProp !== undefined && onActivePanelChange !== undefined
  const activePanel = isControlled ? activePanelProp : internalActivePanel
  const setActivePanel = useCallback(
    (next: RightPanelState) => {
      if (isControlled && onActivePanelChange) {
        onActivePanelChange(next)
      } else {
        setInternalActivePanel(next)
      }
    },
    [isControlled, onActivePanelChange]
  )
  // Convenience accessor — the recently-completed button toggles
  // between this kind and ``closed``.
  const showResolved = activePanel.kind === 'recently-completed'
  const setShowResolved = useCallback(
    (open: boolean) => {
      setActivePanel(open ? { kind: 'recently-completed' } : { kind: 'closed' })
    },
    [setActivePanel]
  )

  // Capture the parent-provided sider host element after mount so the
  // portal has a target to render into. The ref is null during the
  // initial render (refs populate during commit); useMountEffect runs
  // post-commit, sets state, triggers a re-render with the target.
  //
  // Reviewer note (do not "fix"): past reviews have suggested swapping
  // useMountEffect for ``useEffect(..., [resolvedSiderHostRef])`` to
  // handle remount. Two reasons not to: (1) client/CLAUDE.md forbids
  // useEffect outside the useMountEffect escape hatch — derived state,
  // event handlers, or ``key``-based remounts are the sanctioned tools.
  // (2) useMountEffect is per-instance; a real remount (error-boundary
  // recovery, hot reload, parent ``key`` change) creates a new instance
  // and re-runs this hook, so the remount path already works.
  const [siderHostEl, setSiderHostEl] = useState<HTMLElement | null>(null)
  useMountEffect(() => {
    if (resolvedSiderHostRef?.current) {
      setSiderHostEl(resolvedSiderHostRef.current)
    }
  })
  const api = useApiService()
  const { data: companyData } = api.useGetCompanyDetails()
  // Prefetch the data the embedded compose + note editors block on inside
  // their useEffect. Without this, clicking Apply right after a reload
  // mounts the modal before these queries resolve, leaving Quill
  // uninitialized (empty body, no toolbar) AND leaving the compose
  // form's from_id at 0 — which fails zod validation silently and the
  // submit handler never fires. We gate Apply on both queries below so
  // the dialog can't open early.
  const { isLoading: isUserOptionsLoading } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: false,
  })
  const { data: integrationOptions, isLoading: isIntegrationOptionsLoading } =
    api.emailIntegration.useGetEmailIntegrationOptions()
  const isEditorDataReady =
    !isUserOptionsLoading && !isIntegrationOptionsLoading
  // Whether the rec owner has at least one connected email integration
  // capable of sending. ``can_send`` is the backend-derived "matching
  // email + synced + not expired" flag. Forwarded to the apply panel
  // so a ``send_email`` prepared action is force-disabled when there's
  // no integration.
  const hasEmailIntegration = (integrationOptions ?? []).some(
    (opt) => opt.original?.can_send
  )
  // Recommendation agents come from the backend AGENT_REGISTRY
  // (server/recommendations/pipeline/registry.py) via the company-details
  // payload. The frontend never hardcodes which signal_types belong to
  // which agent — that decision lives backend-side so the two cannot drift.
  const recommendationAgents = useMemo(
    () => companyData?.recommendation_agents ?? [],
    [companyData?.recommendation_agents]
  )
  const signalTypeToAgentId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const agent of recommendationAgents) {
      for (const signalType of agent.signal_types) {
        map[signalType] = agent.id
      }
    }
    return map
  }, [recommendationAgents])
  // Agent sections used by the "Type" view. Filter out agents whose every
  // signal type is disabled — a fully-off agent has no actions to show
  // and rendering an empty section just adds visual noise.
  const filteredAgentSections = useMemo(
    () =>
      recommendationAgents
        .filter((a) => a.enabled)
        .map((a) => ({
          key: a.id,
          title: a.label,
          Icon: resolveAgentIcon(a.icon),
          iconColor: 'text-fg-secondary',
        })),
    [recommendationAgents]
  )
  const viewTabs = useMemo(
    () => [
      { key: 'priority' as ViewMode, title: 'Priority' },
      { key: 'signal' as ViewMode, title: 'Type' },
      { key: 'company' as ViewMode, title: 'Company' },
    ],
    []
  )

  const summaryQuery = useQuery<ActionSummaryData | null, Error>({
    queryKey: ['recommendations', userId ?? 'self'],
    queryFn: async () => {
      try {
        return await getLatestActionSummary(userId)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          axiosError.response?.data?.detail ||
            axiosError.message ||
            'Failed to fetch recommendations'
        )
      }
    },
    refetchOnWindowFocus: false,
  })

  const summary = summaryQuery.data

  // Polling for GENERATING status
  useRecommendationsPolling({
    summary,
    refetch: summaryQuery.refetch,
    userId,
  })

  // Poll for recommendation refresh status — when CRM entities change, the
  // backend queues an LLM re-evaluation. This refetches the summary on completion.
  const { refreshStatus } = useAggregationRefresh({
    queryKey: ['recommendations-refresh-status'],
    queryFn: getRecommendationRefreshStatus,
    getIsRefreshing: (data) => data.is_refreshing,
    onRefetch: useCallback(() => summaryQuery.refetch(), [summaryQuery]),
    refetchInterval: 5000,
    enabled: userId === undefined,
  })

  // Set of fingerprints currently being re-evaluated — only those cards
  // get disabled/skeleton treatment, not the entire list.
  const refreshingFingerprints = useMemo(
    () => new Set(refreshStatus?.refreshing_fingerprints ?? []),
    [refreshStatus?.refreshing_fingerprints]
  )

  useEffect(() => {
    onSummaryLoaded?.(summary ?? null)
  }, [summary, onSummaryLoaded])

  useEffect(() => {
    setShowResolved(false)
  }, [summary?.id, userId])

  // Action click management
  const {
    handleActionClick,
    handleStatusClick,
    getStatusForAction,
    getAutoCompletedForAction,
  } = useActionClickManager({ summary })

  // The modal POSTs to the atomic apply endpoint itself; this callback
  // just refreshes the data once the backend has marked the rec resolved
  // and recorded the click. If the apply failed, the modal stays open
  // with an error toast and we never get here.
  const handleAppliedRecommendation = async (action: EnrichedAction) => {
    queryClient.removeQueries({
      queryKey: ['deal', String(action.deal_id)],
    })
    await summaryQuery.refetch()
  }

  // Non-action resolution path: user unchecked every prepared action
  // and clicked Apply, so we just mark the rec done client-side
  // (no server call to apply needed).
  const handleNoOpResolution = async (action: EnrichedAction) => {
    await handleStatusClick(action, 'done', false)
    await summaryQuery.refetch()
  }

  const allActions = useMemo(
    () => summary?.summary_data?.enriched_actions ?? [],
    [summary]
  )
  const { activeActions, resolvedActions } = useMemo(
    () => partitionActionsByStatus(allActions, getStatusForAction),
    [allActions, getStatusForAction]
  )

  const actionsByUrgency = useMemo(
    () => sortByUrgencyScore(activeActions),
    [activeActions]
  )
  const actionsByAgent = useMemo(
    () => groupActionsByAgent(activeActions, signalTypeToAgentId),
    [activeActions, signalTypeToAgentId]
  )
  // Keyed agent map for resolved actions — still drives `hasSignalData` (which
  // tab/stats show). The completed panel re-groups the *filtered* set at render
  // time, so the per-view groupings live in renderResolvedSections.
  const resolvedActionsByAgent = useMemo(
    () => groupActionsByAgent(resolvedActions, signalTypeToAgentId),
    [resolvedActions, signalTypeToAgentId]
  )
  const actionsByCompany = useMemo(
    () => groupActionsByCompany(activeActions),
    [activeActions]
  )

  // ── Recently-completed panel: client-side search ────────────────────────
  // The resolved actions are already in memory, so search is purely a frontend
  // concern — no extra API call. One free-text box matches title + company +
  // deal + contact (declared above the early returns so hook order is stable).
  const [completedSearch, setCompletedSearch] = useState('')

  const filteredResolvedActions = useMemo(() => {
    const q = completedSearch.trim().toLowerCase()
    if (!q) return resolvedActions
    // Broad match — entity fields plus the action title and recommendation
    // text (the placeholder says "…or keyword" to set that expectation).
    return resolvedActions.filter((a) =>
      [a.action, a.recommendation, a.company_name, a.deal_name, a.contact_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [resolvedActions, completedSearch])

  // Group the filtered resolved set for the active view — memoized (not
  // recomputed inline in render) and keyed on the filtered set + view so it
  // only recomputes when those actually change. Discriminated by `mode` so the
  // renderer narrows cleanly.
  const resolvedGroups = useMemo(() => {
    switch (viewMode) {
      case 'signal':
        return {
          mode: 'signal' as const,
          byAgent: groupActionsByAgent(
            filteredResolvedActions,
            signalTypeToAgentId
          ),
        }
      case 'company':
        return {
          mode: 'company' as const,
          byCompany: groupActionsByCompany(filteredResolvedActions),
        }
      default:
        return {
          mode: 'priority' as const,
          days: groupByCompletionDay(filteredResolvedActions),
        }
    }
  }, [filteredResolvedActions, viewMode, signalTypeToAgentId])

  /**
   * Wrap a status-specific body (loading skeleton, error card, no-summary
   * card, FAILED card, GENERATING card) in the same shell the data-rich
   * path uses. The shell keeps ``headerSlot`` mounted so the user picker,
   * super-user regen button, and collapse control stay reachable in every
   * status — without this, hitting an error would strand a superuser on
   * a single failing summary with no way to switch to a different user
   * or kick a regen. The "Manage AI Agents" footer is anchored by the
   * parent layout (sibling, not child), so it survives independently.
   */
  const renderStatusShell = (body: React.ReactNode): React.ReactNode => (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      <div className="flex-shrink-0 pb-4 border-b border-line bg-surface -mx-6 px-6">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">{headerSlot}</div>
          {headerTrailingSlot}
        </div>
      </div>
      <div className="flex-1 overflow-auto pt-4 pb-6">{body}</div>
    </div>
  )

  if (summaryQuery.isLoading) {
    return renderStatusShell(
      <div className="flex flex-col gap-4">
        {/* Stats header placeholder */}
        <div className="flex items-center gap-4 animate-pulse">
          <div className="h-5 w-32 rounded bg-surface-disabled" />
          <div className="h-5 w-20 rounded bg-surface-disabled" />
          <div className="h-5 w-20 rounded bg-surface-disabled" />
        </div>
        {/* Card placeholders — same component used for the per-card
         *  applying state so the visuals are consistent. */}
        {[0, 1, 2, 3].map((i) => (
          <EnrichedActionCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const queryError = summaryQuery.error as Error | undefined

  if (queryError) {
    return renderStatusShell(
      <div className="rounded-lg border !border-alert-danger-border bg-alert-danger-bg p-6">
        <div className="flex items-center gap-2 text-alert-danger-title">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">Error loading recommendations</span>
        </div>
        <p className="mt-2 text-sm text-alert-danger-title">
          {queryError.message}
        </p>
      </div>
    )
  }

  // No summary exists yet (status is null)
  if (!summary || summary.status === null) {
    return renderStatusShell(
      <div className="rounded-lg border border-line bg-surface p-6 shadow-sm">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-fg-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-fg">
            No recommendations available
          </h3>
        </div>
      </div>
    )
  }

  if (summary.status === 'FAILED') {
    return renderStatusShell(
      <div className="rounded-lg border !border-alert-warning-border bg-alert-warning-bg p-6">
        <div className="flex items-center gap-2 text-alert-warning-title">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-medium">Summary generation failed</span>
        </div>
        {summary.error_message && (
          <p className="mt-2 text-sm text-alert-warning-title">
            {summary.error_message}
          </p>
        )}
      </div>
    )
  }

  if (summary.status === 'GENERATING') {
    return renderStatusShell(
      <div className="rounded-lg border border-line bg-surface p-6">
        <div className="flex items-center gap-2 text-fg-tertiary">
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="font-medium">
            Generating your recommendations...
          </span>
        </div>
        <p className="mt-2 text-sm text-fg-secondary">
          This usually takes 10-30 seconds
        </p>
      </div>
    )
  }

  const hasActions = allActions.length > 0
  const hasActiveActions = activeActions.length > 0
  const hasResolvedActions = resolvedActions.length > 0
  const hasSignalData =
    Object.keys(actionsByAgent).length > 0 ||
    (showResolved && Object.keys(resolvedActionsByAgent).length > 0)

  /** Shared empty state shown when no active actions exist in any view. */
  const emptyState = (
    <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-5 text-center">
      <p className="text-base font-medium text-fg">No active recommendations</p>
      <p className="mt-1 text-base text-fg-secondary">
        {hasResolvedActions
          ? 'Resolved items are hidden from your active queue.'
          : 'Your pipeline looks healthy.'}
      </p>
    </div>
  )

  /**
   * Render a group header matching the priority-view band style:
   * icon + label + muted count.
   */
  const renderGroupHeader = (Icon: React.ElementType, label: string) => (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={16} className="text-fg-secondary" />
      <span className="text-sm font-semibold uppercase tracking-wide text-fg-secondary">
        {label}
      </span>
    </div>
  )

  /**
   * Render the resolved-actions section for the side panel, grouped by
   * the user's currently-selected viewMode. Same data structures used
   * by the active-action sections, just sourced from the resolved
   * memos so the panel mirrors the user's mental model of how cards
   * are organized.
   */
  const renderResolvedSections = () => {
    // Filters can exclude everything even though completed items exist.
    if (filteredResolvedActions.length === 0) {
      return (
        <p className="text-base text-fg-secondary text-center mt-8">
          No completed items match your filters.
        </p>
      )
    }
    if (resolvedGroups.mode === 'priority') {
      // Completed work is chronological, so group by completion day with
      // timeline-style date headers (label + divider) instead of urgency bands.
      return (
        <div className="space-y-4">
          {resolvedGroups.days.map((group) => (
            <div key={group.key} className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-sm text-fg-secondary whitespace-nowrap tabular-nums">
                  {group.label}
                </span>
                <div className="h-px flex-1 bg-line" />
              </div>
              {renderCards(group.items, `day-resolved-${group.key}`, false)}
            </div>
          ))}
        </div>
      )
    }
    if (resolvedGroups.mode === 'signal') {
      const { byAgent } = resolvedGroups
      return (
        <div className="space-y-4">
          {filteredAgentSections.map(({ key, title, Icon }) => {
            const sectionActions = byAgent[key] ?? []
            if (sectionActions.length === 0) return null
            return (
              <div key={`resolved-${key}`}>
                {renderGroupHeader(Icon, title)}
                {renderCards(sectionActions, `agent-resolved-${key}`, false)}
              </div>
            )
          })}
        </div>
      )
    }
    // company view
    return (
      <div className="space-y-4">
        {resolvedGroups.byCompany.map((company) => (
          <div key={`resolved-${company.companyId}`}>
            {renderGroupHeader(PiBuildings, company.companyName)}
            {renderCards(
              company.actions,
              `company-resolved-${company.companyId}`,
              false
            )}
          </div>
        ))}
      </div>
    )
  }

  /**
   * Render a list of action cards for a group.
   *
   * ``dimWhenResolved`` defaults to true, which is the right setting
   * for the main active-list (already-resolved cards fade visually so
   * the user's eye can skip them). The recently-completed side panel
   * passes ``false`` so its cards render at full opacity.
   */
  const renderCards = (
    actions: EnrichedAction[],
    keyPrefix: string,
    dimWhenResolved: boolean = true
  ) => (
    <div className="space-y-4">
      {actions.map((action, index) => (
        <EnrichedActionCard
          key={`${keyPrefix}-${action.signal_fingerprint ?? `${action.deal_id}-${index}`}`}
          action={action}
          dimWhenResolved={dimWhenResolved}
          onCardClick={() =>
            handleActionClick(action, action.signal_type, action.action_type)
          }
          readOnly={!!userId}
          isRefreshing={
            // Disable Apply/Dismiss while any of:
            //  (a) the per-rec refresh poller flagged this signal (the
            //      LLM is re-evaluating it),
            //  (b) we're refetching the whole summary (e.g. right after
            //      a successful apply, before the new resolution state
            //      lands), or
            //  (c) the user-options query the embedded Quill editors
            //      block on hasn't resolved yet — without this, the
            //      modal can mount before Quill initializes and shows
            //      empty body editors with no toolbar.
            refreshingFingerprints.has(action.signal_fingerprint ?? '') ||
            summaryQuery.isFetching ||
            !isEditorDataReady
          }
          onStatusClick={(status) => handleStatusClick(action, status)}
          actionStatus={getStatusForAction(action)}
          actionAutoCompleted={getAutoCompletedForAction(action)}
          onApplied={() => handleAppliedRecommendation(action)}
          onNoOpResolve={() => handleNoOpResolution(action)}
          onApplyClick={(a) => setActivePanel({ kind: 'apply-rec', action: a })}
          recommendationAgents={recommendationAgents}
        />
      ))}
    </div>
  )

  // No actions in any view → reuse the status shell so the user picker,
  // super-user regen, and collapse stay in the same fixed-position header
  // they occupy in error / loading / generating states. The data-rich
  // path below has its own header-with-stats wrapper since it also needs
  // to layer in SummaryStatsHeader and the view-mode tabs.
  if (!hasActions) {
    return renderStatusShell(
      <div className="rounded-lg border border-dashed border-line bg-surface p-6 text-center">
        <p className="text-base font-medium text-fg">
          No active recommendations
        </p>
        <p className="mt-1 text-base text-fg-secondary">
          Your pipeline looks healthy.
        </p>
      </div>
    )
  }

  // ``hasActions`` is true at this point — the no-actions branch above
  // returned early via ``renderStatusShell``. The wrapper below is the
  // data-rich layout: pinned header (slot + stats + view controls) and
  // a scrollable body of grouped action cards.
  return (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      {/* Fixed header zone: title row + stats. Outer border-b is the
          canonical separator for every state (with/without stats); the
          previous `h-px` wrapper only fired when summary_data was loaded. */}
      <div className="flex-shrink-0 bg-surface -mx-6 px-6 pb-4 flex flex-col gap-4 border-b border-line">
        {/* Title row: card identity + "Group by". Group by lives up here
            because it regroups the stats AND the cards, so it's a global view
            control rather than a list-row control. */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">{headerSlot}</div>
          {(hasSignalData || hasResolvedActions || summary.created) && (
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-fg-secondary whitespace-nowrap">
                Group by
              </span>
              <PillTabs<ViewMode>
                aria-label="Group recommendations by"
                idPrefix={REC_TABS_ID}
                size="sm"
                tabs={viewTabs}
                activeKey={viewMode}
                onChange={setViewMode}
              />
            </div>
          )}
        </div>

        {/* Toolbar row: list summary stats (left) and the list view controls
            — "View completed" + "Group by" (right). Grouped on one line
            because they all act on the list below. */}
        {(summary.summary_data ||
          hasSignalData ||
          hasResolvedActions ||
          summary.created) && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              {summary.summary_data && (
                <SummaryStatsHeader
                  data={summary.summary_data}
                  visibleActions={activeActions}
                  viewMode={viewMode}
                  companiesCount={actionsByCompany.length}
                  recommendationAgents={recommendationAgents}
                />
              )}
            </div>
            {(hasSignalData || hasResolvedActions || summary.created) && (
              <div className="flex items-center gap-3 flex-wrap flex-shrink-0">
                {(summaryQuery.isFetching ||
                  refreshingFingerprints.size > 0 ||
                  !isEditorDataReady) && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-fg-muted">
                    <PiCircleNotch size={14} className="animate-spin" />
                    Updating…
                  </span>
                )}
                {headerTrailingSlot}
                <button
                  type="button"
                  onClick={() => setShowResolved(!showResolved)}
                  className={cn(
                    secondaryPillButtonClass,
                    'gap-1.5 min-h-8 px-2.5 text-sm'
                  )}
                >
                  <PiListChecks size={14} className="flex-shrink-0" />
                  View completed
                  <span className="inline-flex items-center rounded-full bg-neutral-200 px-1.5 py-0.5 text-xs font-medium leading-none tabular-nums text-neutral-700">
                    {resolvedActions.length}/
                    {activeActions.length + resolvedActions.length}
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrollable action cards */}
      <div className="flex-1 overflow-auto space-y-4 pt-4 pb-6 bg-surface-sunken -mx-6 px-6">
        <TabPanel<ViewMode>
          idPrefix={REC_TABS_ID}
          tabKey="priority"
          activeKey={viewMode}
          className="space-y-4"
        >
          {hasActiveActions ? (
            <div className="space-y-8">
              {URGENCY_BANDS.map(({ key, label }) => {
                const UrgencyIcon = URGENCY_ICONS[key]
                const bandActions = actionsByUrgency.filter(
                  (a) => a.urgency === key
                )
                if (bandActions.length === 0) return null
                return (
                  <div key={key}>
                    {renderGroupHeader(UrgencyIcon, label)}
                    {renderCards(bandActions, `priority-${key}`)}
                  </div>
                )
              })}
            </div>
          ) : (
            emptyState
          )}
        </TabPanel>

        <TabPanel<ViewMode>
          idPrefix={REC_TABS_ID}
          tabKey="signal"
          activeKey={viewMode}
          className="space-y-4"
        >
          {hasActiveActions ? (
            <div className="space-y-4">
              {filteredAgentSections.map(({ key, title, Icon }) => {
                const sectionActions = actionsByAgent[key] ?? []
                if (sectionActions.length === 0) return null
                return (
                  <div key={key}>
                    {renderGroupHeader(Icon, title)}
                    {renderCards(sectionActions, `agent-${key}`)}
                  </div>
                )
              })}
            </div>
          ) : (
            emptyState
          )}
        </TabPanel>

        <TabPanel<ViewMode>
          idPrefix={REC_TABS_ID}
          tabKey="company"
          activeKey={viewMode}
          className="space-y-4"
        >
          {hasActiveActions ? (
            <div className="space-y-4">
              {actionsByCompany.map((company) => (
                <div key={company.companyId}>
                  {renderGroupHeader(PiBuildings, company.companyName)}
                  {renderCards(company.actions, `company-${company.companyId}`)}
                </div>
              ))}
            </div>
          ) : (
            emptyState
          )}
        </TabPanel>
      </div>

      {/* Right-panel content — content portaled into the parent-
          provided host element so it lives as a flex sibling of the
          main content (matches the old Action Center pattern). The
          host's width animates between 0 and 945px; ``overflow:
          hidden`` on the host clips the panel cleanly during the
          transition. The panel itself has no positioning — it just
          fills the host. */}
      {siderHostEl &&
        createPortal(
          <>
            {activePanel.kind === 'recently-completed' && (
              <RightPanel
                onClose={() => setActivePanel({ kind: 'closed' })}
                header={
                  <>
                    <h3 className="text-xl font-semibold text-fg leading-tight truncate min-w-0">
                      Recently completed
                    </h3>
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-medium tracking-[-0.2px] tabular-nums flex-shrink-0 bg-neutral-100 text-neutral-700">
                      {resolvedActions.length}/
                      {activeActions.length + resolvedActions.length}
                    </span>
                  </>
                }
                subHeader={
                  hasResolvedActions ? (
                    <Ds2026SearchInput
                      value={completedSearch}
                      onChange={setCompletedSearch}
                      placeholder="Search by company, deal, contact, or keyword"
                      aria-label="Search completed actions"
                    />
                  ) : undefined
                }
              >
                {hasResolvedActions ? (
                  renderResolvedSections()
                ) : (
                  <p className="text-base text-fg-secondary text-center mt-8">
                    Nothing completed yet.
                  </p>
                )}
              </RightPanel>
            )}
            {activePanel.kind === 'apply-rec' && (
              <ApplyRecommendationPanel
                onClose={() => setActivePanel({ kind: 'closed' })}
                action={activePanel.action}
                preparedActions={activePanel.action.prepared_actions ?? []}
                onApply={async (formData, count) => {
                  const action = activePanel.action
                  if (action.id == null) return
                  // Close the panel immediately and fire the apply in
                  // the background — same UX the modal had. The card's
                  // refresh poller will reflect the new state once the
                  // backend resolves.
                  setActivePanel({ kind: 'closed' })
                  try {
                    await applyRecommendation(action.id, formData)
                    notifySuccess(
                      `${count} ${count === 1 ? 'action' : 'actions'} applied`
                    )
                    handleAppliedRecommendation(action)
                  } catch {
                    // baseAPICall already surfaced an error toast.
                  }
                }}
                onNoOpResolve={() => {
                  handleNoOpResolution(activePanel.action)
                }}
                mutuallyExclusiveActions={
                  summary?.summary_data?.mutually_exclusive_actions ?? []
                }
                hasEmailIntegration={hasEmailIntegration}
                recommendationAgents={recommendationAgents}
              />
            )}
          </>,
          siderHostEl
        )}
    </div>
  )
}
